/// <amd-module name="Repos/Common/Util/File" />
import * as Resources from "Repos/Common/Resources";
import { format } from "VSS/Core/Util/String";

/// <amd-module name="Repos/Common/Util/File" />
/**
 * Gets the file name from an item path
 *
 * @param path Full path of the item
 */
export function getFileName(path: string): string {
    if (path) {
        const lastSegment = path.lastIndexOf("/");
        return lastSegment >= 0 ? path.substr(lastSegment + 1) : path;
    } else {
        return "";
    }
}

/**
 * Gets the file extension for the given file name
 *
 * @param name Name of the file
 */
export function getFileExtension(name: string): string {
    const dotIndex = name.lastIndexOf(".");
    return dotIndex >= 0 ? name.substr(dotIndex + 1) : "";
}

/**
 * Gets the icon name to use for the given file
 *
 * @param name Name of the file
 */
export function getIconNameForFile(name: string) {
    const extension = getFileExtension(name).toLowerCase();
    switch (extension) {
        case "aspx":
            return "FileASPX";
        case "bat":
        case "cmd":
            return "Script";
        case "c":
        case "cc":
        case "h":
        case "hpp":
        case "cpp":
        case "cxx":
            return "CPlusPlusLanguage";
        case "coffee":
        case "litcoffee":
            return "CoffeeScript";
        case "cs":
            return "CSharpLanguage";
        case "css":
            return "FileCSS";
        case "doc":
        case "docx":
            return "WordDocument";
        case "fs":
        case "fsx":
            return "FSharpLanguage";
        case "htm":
        case "html":
            return "FileHTML";
        case "java":
            return "FileJAVA";
        case "js":
            return "JavaScriptLanguage";
        case "less":
            return "FileLess";
        case "md":
        case "markdown":
        case "mdown":
            return "MarkDownLanguage";
        case "pdb":
            return "FilePDB";
        case "ppt":
        case "pptx":
            return "PowerPointDocument";
        case "ps1":
        case "psm1":
            return "PowerShell";
        case "py":
            return "PythonLanguage";
        case "sass":
            return "FileSass";
        case "sln":
            return "FileTypeSolution";
        case "sql":
            return "FileSQL";
        case "ts":
        case "tsx":
            return "TypeScriptLanguage";
        case "xml":
            return "Embed";
        case "yml":
        case "yaml":
            return "FileYML";
        default:
            return "Page";
    }
}

/**
 * Gets the parent folder path for the given item path
 *
 * @param path Parent folder path
 */
export function getParentPath(path: string, root = "/"): string | undefined {
    let parentPath: string | undefined;
    const trimmedPath = trimTrailingSlash(trimLeadingSlash(path, root));
    if (trimmedPath) {
        parentPath = root;
        const lastSlash = trimmedPath.lastIndexOf("/");
        if (lastSlash >= 0) {
            parentPath += trimmedPath.substr(0, lastSlash);
        }
    }
    return parentPath;
}

const invalidFileChars = new Set(['"', "/", ":", "<", ">", "\\", "*", "?"]);

/**
 * Determine if the given filename is a legal git file name and return undefined if so,
 * otherwise a message that indicates the reason why it is invalid.
 *
 * @param name file name
 */
export function getInvalidFileNameMessage(name: string): string | undefined {
    if (Array.from(name).some(c => invalidFileChars.has(c))) {
        return format(Resources.FileInvalidCharactersFormat, name);
    } else if (name.endsWith(".") || name.endsWith(" ")) {
        return format(Resources.FileInvalidEndCharactersFormat, name);
    }

    return undefined;
}

/**
 * Trims the root "/" at the start of the given path
 *
 * @param path Path that may begin with "/"
 */
export function trimLeadingSlash(path: string, root = "/"): string {
    return path && path.startsWith(root) ? path.substr(root.length) : path;
}

/**
 * Trims any trailing slash "/" at the end of the given path
 *
 * @param path Path that may end with "/"
 */
export function trimTrailingSlash(path: string): string {
    return path && path[path.length - 1] === "/" ? path.substr(0, path.length - 1) : path;
}
